import classNames from 'classnames'
import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { PAGES_WITHOUT_SEARCHBOX } from 'shared/consts'
import {
  EXPERIMENT_VARIATION,
  NOW_PDP_SERP_GB_FUNCTIONALITY,
} from 'shared/experiments/consts'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { type IState, type NowDataState } from 'types/redux'
import { type UserData } from 'views/components/organisms/CmsMobileMenu/types'
import NowGlobalBarContentBlueVariant from 'views/components/organisms/NowGlobalBarContentBlueVariant'
import NowGlobalBarContentGreenVariant from 'views/components/organisms/NowGlobalBarContentGreenVariant'
import Experiment from 'views/components/utils/optimizely/Experiment'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { fetchCityByZipCode } from 'views/utils/nowGlobalBar'
import './NowGlobalBar.scss'

interface INowGlobalBarProps {
  publicRuntimeConfig: {
    pageProperties: {
      pageType: string
    }
  }
}

const NowGlobalBar: (props: INowGlobalBarProps) => ReactElement = props => {
  const {
    publicRuntimeConfig: {
      pageProperties: { pageType },
    },
  } = props
  const dispatch = useDispatch()
  const nowData: NowDataState = useSelector((state: IState) => state.nowData)

  const userData = useSelector((state: IState) => state.userSession) as UserData

  const [openNowZipModal, setOpenNowZipModal] = useState(false)
  const [zip, setZip] = useState(nowData?.zipCode || '')
  const [city, setCity] = useState(nowData?.city || '')
  const [isInitialZipCodeSet, setIsInitialZipCodeSet] = useState(false)

  const storeCityData = useCallback(async () => {
    let cityToSet = ''

    if (!nowData?.zipCode) {
      setCity(cityToSet)
    } else {
      cityToSet = (await fetchCityByZipCode(nowData?.zipCode)) ?? ''
      setCity(cityToSet)
    }
    dispatch(
      updateNowDataSuccessAction({
        ...nowData,
        city: cityToSet,
        refreshNowData: true,
        fetchCity: false,
      })
    )
  }, [dispatch, nowData])

  useEffect(() => {
    if (!isInitialZipCodeSet) {
      const data = localStorage.getItem('nowData')
      const defaultNowData = data ? JSON.parse(data) : null

      setZip(defaultNowData?.zipCode)
      let fetchCity = true
      if (defaultNowData?.city) {
        setCity(defaultNowData?.city)
        fetchCity = false
        setIsInitialZipCodeSet(true)
      }

      dispatch(
        updateNowDataSuccessAction({
          ...defaultNowData,
          fetchCity,
          refreshNowData: !fetchCity,
        })
      )
    }
  }, [dispatch, isInitialZipCodeSet, storeCityData])

  useEffect(() => {
    if (!isInitialZipCodeSet && nowData?.fetchCity) {
      void storeCityData()

      setIsInitialZipCodeSet(true)
    }
  }, [isInitialZipCodeSet, nowData?.fetchCity, storeCityData])

  useEffect(() => {
    if (isInitialZipCodeSet && zip !== nowData?.zipCode) {
      setZip(nowData?.zipCode)
      void storeCityData()
    }
  }, [isInitialZipCodeSet, nowData?.zipCode, storeCityData, zip])

  const pageHasNoSearchBarInHeader = PAGES_WITHOUT_SEARCHBOX.includes(pageType)
  const classes = classNames('c-NowGlobalBar', {
    'c-NowGlobalBar-with-search-box': !pageHasNoSearchBarInHeader,
    'c-NowGlobalBar-without-search-box': pageHasNoSearchBarInHeader,
  })

  return (
    <Experiment name={NOW_PDP_SERP_GB_FUNCTIONALITY}>
      {({ isEnabled, variant }) =>
        isEnabled && (
          <div data-clientside-hook="h-NowGlobalBar" className={classes}>
            {variant === EXPERIMENT_VARIATION.V1 && (
              <NowGlobalBarContentBlueVariant
                zip={zip}
                city={city}
                openNowZipModal={openNowZipModal}
                setOpenNowZipModal={setOpenNowZipModal}
              />
            )}
            {variant === EXPERIMENT_VARIATION.V2 && (
              <NowGlobalBarContentGreenVariant
                zip={zip}
                city={city}
                openNowZipModal={openNowZipModal}
                setOpenNowZipModal={setOpenNowZipModal}
                userData={userData}
              />
            )}
          </div>
        )
      }
    </Experiment>
  )
}

export default withShopAndRuntimeConfig(NowGlobalBar)
